import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';

interface UseThreadMarkReadOptions {
  /**
   * Async fn that POSTs the module's mark-read endpoint (e.g.
   * `() => ticketApi.markRead(ticketId)`). Returns when the watermark is saved.
   */
  markRead: () => Promise<unknown>;
  /**
   * Query key(s) carrying the entity's `unread_count` (usually the detail
   * query). Invalidated after a successful mark-read so the badge clears.
   */
  invalidate: QueryKey | QueryKey[];
  /** When false the returned callback is inert (e.g. id not yet known). */
  enabled?: boolean;
}

interface UseThreadMarkReadResult {
  /** Fire-and-forget mark-read; safe to call repeatedly (no-op while pending). */
  markAsRead: () => void;
  isMarking: boolean;
}

function isNestedKeys(invalidate: QueryKey | QueryKey[]): invalidate is QueryKey[] {
  return Array.isArray(invalidate[0]);
}

/**
 * useThreadMarkRead — the read-state companion to `useThreadLiveRefresh`.
 *
 * Marks an entity's message thread read for the current user and invalidates
 * the query holding its `unread_count` so a `ThreadUnreadBadge` clears. The
 * module supplies its own mark-read call + invalidation keys; the mechanics
 * (single-flight mutation, invalidation) are shared. Pair with
 * `useThreadLiveRefresh` so an inbound reply bumps the badge live.
 *
 * Adoption is two steps: (1) backend mark-read endpoint + `unread_count`/
 * `message_count` on the entity response; (2) frontend — call `markAsRead()`
 * when the thread is opened and render `ThreadUnreadBadge`.
 */
export function useThreadMarkRead({
  markRead,
  invalidate,
  enabled = true,
}: UseThreadMarkReadOptions): UseThreadMarkReadResult {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: markRead,
    onSuccess: () => {
      const keys = isNestedKeys(invalidate) ? invalidate : [invalidate];
      keys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    },
  });

  // Latest mutation + enabled flag via refs so `markAsRead` stays referentially
  // stable. Consumers commonly call it from an effect keyed on message count; an
  // unstable callback (changing as `isPending` toggles) would re-run that effect
  // and re-fire the mutation in a loop.
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Synchronous single-flight latch: `isPending` is React state and updates
  // asynchronously, so two calls in the same tick would both slip through. This
  // ref flips immediately and resets when the mutation settles.
  const inFlightRef = useRef(false);

  const markAsRead = useCallback(() => {
    if (!enabledRef.current || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    mutationRef.current.mutate(undefined, {
      onSettled: () => {
        inFlightRef.current = false;
      },
    });
  }, []);

  return { markAsRead, isMarking: mutation.isPending };
}
